import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import { INextPage } from '@web/gallery/core/models/gallery';

import * as mediaFilesActions from '../actions/live-media-files.actions';

import ILiveMediaFile = Microsoft.VideoIndexer.Live.Contracts.ILiveMediaFile;
import ILiveInsightType = Microsoft.VideoIndexer.Live.Contracts.ILiveIncludedInsightType;
import ILiveTag = Microsoft.VideoIndexer.Live.Contracts.ILiveTag;

export interface MediaFile extends Partial<ILiveMediaFile> {
  insightsTypes?: ILiveInsightType[];
  isInsightsTypesLoaded?: boolean;
  error?: boolean;
  tags?: ILiveTag[]; // Tags associated with this media file
  areTagsLoading?: boolean;
  areTagsUpdating?: boolean;
  tagsError?: string;
  tagsUpdateError?: string;
}

export interface IState extends EntityState<MediaFile> {
  loaded: boolean;
  isLoading: boolean;
  isDeleting: boolean;
  isRenaming: boolean;
  error: boolean;
  nextPage: INextPage;
  currentCameraMediaFiles: {
    mediaFiles: MediaFile[];
    isLoading: boolean;
    loaded: boolean;
    error: boolean;
    nextPage: INextPage;
  };
}

export function selectMediaFileId(mediaFile: MediaFile): string {
  return mediaFile.id;
}

export const adapter: EntityAdapter<MediaFile> = createEntityAdapter<MediaFile>({
  selectId: selectMediaFileId
});

const nextPage = {
  pageSize: null,
  skip: 0,
  done: false,
  totalCount: null
};

// Update the initial state
export const initialState: IState = adapter.getInitialState({
  loaded: false,
  isLoading: false,
  isDeleting: false,
  isRenaming: false,
  error: false,
  nextPage,
  currentCameraMediaFiles: {
    mediaFiles: [],
    isLoading: false,
    loaded: false,
    error: false,
    nextPage
  }
});

const liveMediaFilesReducer = createReducer(
  initialState,
  on(mediaFilesActions.listMediaFilesStarted, state => {
    return {
      ...state,
      isLoading: true,
      loaded: !!state.nextPage.skip
    };
  }),
  on(mediaFilesActions.listMediaFilesSucceeded, (state, { mediaFiles, nextPage }) => {
    const newState = {
      ...state,
      isLoading: false,
      loaded: true,
      error: false,
      nextPage
    };

    return adapter.addMany(mediaFiles, newState);
  }),
  on(mediaFilesActions.listMediaFilesFailed, (state, { error }) => {
    return {
      ...state,
      isLoading: false,
      loaded: true,
      error: true
    };
  }),
  on(mediaFilesActions.clearMediaFiles, state => {
    return adapter.removeAll({
      ...state,
      loaded: false,
      isLoading: false,
      error: false,
      nextPage: initialState.nextPage
    });
  }),
  on(mediaFilesActions.getMediaFileSucceeded, (state, { mediaFile }) => {
    return adapter.upsertOne({ ...mediaFile, isInsightsTypesLoaded: true }, state);
  }),
  on(mediaFilesActions.getMediaFileFailed, (state, { mediaFileId }) => {
    return adapter.upsertOne({ id: mediaFileId, error: true }, state);
  }),

  // Add reducers for camera media files actions
  on(mediaFilesActions.listAllCameraMediaFiles, state => {
    const cameraState = state.currentCameraMediaFiles || initialState.currentCameraMediaFiles;

    return {
      ...state,
      currentCameraMediaFiles: {
        ...cameraState,
        isLoading: true,
        loaded: false
      }
    };
  }),
  on(mediaFilesActions.listAllCameraMediaFilesSucceeded, (state, { mediaFiles, nextPage }) => {
    const cameraId = mediaFiles.length > 0 ? mediaFiles[0].source : '';
    if (!cameraId) {
      return state;
    }

    return {
      ...state,
      currentCameraMediaFiles: {
        ...state.currentCameraMediaFiles,
        mediaFiles: mediaFiles.filter(mediaFile => mediaFile.state === 'Processed'),
        isLoading: false,
        loaded: true,
        error: false,
        nextPage
      }
    };
  }),
  on(mediaFilesActions.listAllCameraMediaFilesFailed, (state, { error: _error }) => {
    const cameraId = state.currentCameraMediaFiles && Object.keys(state.currentCameraMediaFiles)[0];
    if (!cameraId) {
      return state;
    }

    return {
      ...state,
      currentCameraMediaFiles: {
        ...state.currentCameraMediaFiles,
        isLoading: false,
        loaded: true,
        error: true
      }
    };
  }),
  on(mediaFilesActions.deleteMediaFile, state => ({
    ...state,
    isDeleting: true
  })),
  on(mediaFilesActions.deleteMediaFileSucceeded, (state, { mediaFileId }) => {
    return adapter.removeOne(mediaFileId, {
      ...state,
      isDeleting: false
    });
  }),
  on(mediaFilesActions.deleteMediaFileFailed, (state, { error }) => {
    return {
      ...state,
      error: true,
      isDeleting: false
    };
  }),

  on(mediaFilesActions.loadVideoTags, (state, { mediaFileId }) => {
    return adapter.upsertOne(
      {
        id: mediaFileId,
        areTagsLoading: true,
        tagsError: null
      },
      state
    );
  }),

  on(mediaFilesActions.loadVideoTagsSucceeded, (state, { mediaFileId, tags }) => {
    return adapter.upsertOne(
      {
        id: mediaFileId,
        tags,
        areTagsLoading: false,
        tagsError: null
      },
      state
    );
  }),

  on(mediaFilesActions.loadVideoTagsFailed, (state, { mediaFileId, error }) => {
    return adapter.upsertOne(
      {
        id: mediaFileId,
        areTagsLoading: false,
        tagsError: error
        //tags: undefined
      },
      state
    );
  }),

  on(mediaFilesActions.updateVideoTags, (state, { mediaFileId }) => {
    return adapter.upsertOne(
      {
        id: mediaFileId,
        areTagsUpdating: true,
        tagsUpdateError: null
      },
      state
    );
  }),

  on(mediaFilesActions.updateVideoTagsSucceeded, (state, { mediaFileId }) => {
    return adapter.upsertOne(
      {
        id: mediaFileId,
        areTagsUpdating: false,
        tagsUpdateError: null
      },
      state
    );
  }),

  on(mediaFilesActions.updateVideoTagsFailed, (state, { mediaFileId, error }) => {
    return adapter.upsertOne(
      {
        id: mediaFileId,
        areTagsUpdating: false,
        tagsUpdateError: error
      },
      state
    );
  }),

  on(mediaFilesActions.renameMediaFile, state => ({
    ...state,
    isRenaming: true
  })),
  on(mediaFilesActions.renameMediaFileSucceeded, (state, { mediaFileId, newName }) => {
    return adapter.updateOne(
      {
        id: mediaFileId,
        changes: { name: newName }
      },
      {
        ...state,
        isRenaming: false
      }
    );
  }),
  on(mediaFilesActions.renameMediaFileFailed, (state, { error }) => {
    return {
      ...state,
      isRenaming: false
    };
  })
);

export function reducer(state: IState | undefined, action: Action) {
  return liveMediaFilesReducer(state, action);
}
